/**
 * The customer care team's live hours, said the way customers are always told them.
 *
 * The team works Monday to Friday, 9am to 5pm Arizona time, and Arizona never moves its
 * clocks. Customers are told Central time, labelled CST, because the company started in
 * Illinois and is still registered there (Ferras, 1 October 2026). So the Central hours a
 * page shows move with Central's daylight saving: 11am to 7pm from March to November, 10am
 * to 6pm from November to March. Working them out here, from the Arizona hours, keeps every
 * page right on both sides of a clock change without anyone editing it. CSBOT's chat and
 * replies do the same (its services/team_hours.py).
 *
 * The day is the team's (Arizona) day, so a page served late in the evening shows that
 * day's hours, not tomorrow's.
 */

// Arizona is UTC-7 all year, so the team's 9am and 5pm are always 16:00 and 24:00 UTC.
const ARIZONA_OFFSET_HOURS = -7;
const TEAM_OPENS_HOUR = 9;
const TEAM_CLOSES_HOUR = 17;
const HOUR_MS = 3_600_000;

const centralHour = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  hour: 'numeric',
  hour12: true,
});

type Clock = [hour: string, period: 'am' | 'pm'];

/** Midnight UTC of the team's (Arizona) date at `now`, in milliseconds. */
function teamDay(now: Date): number {
  const arizona = new Date(now.getTime() + ARIZONA_OFFSET_HOURS * HOUR_MS);
  return Date.UTC(arizona.getUTCFullYear(), arizona.getUTCMonth(), arizona.getUTCDate());
}

/** The Central time of one of the team's Arizona hours on that day. */
function central(day: number, arizonaHour: number): Clock {
  const parts = centralHour.formatToParts(new Date(day + (arizonaHour - ARIZONA_OFFSET_HOURS) * HOUR_MS));
  const hour = parts.find((part) => part.type === 'hour')?.value ?? '';
  const period = parts.find((part) => part.type === 'dayPeriod')?.value.toLowerCase() === 'am' ? 'am' : 'pm';
  return [hour, period];
}

/** "11am to 7pm": today's live hours in Central time. */
export function liveAgentClock(now: Date = new Date()): string {
  const day = teamDay(now);
  const [opens, openPeriod] = central(day, TEAM_OPENS_HOUR);
  const [closes, closePeriod] = central(day, TEAM_CLOSES_HOUR);
  return `${opens}${openPeriod} to ${closes}${closePeriod}`;
}

/** "11:00 a.m. to 7:00 p.m.": the same, for a page that spells times out. */
export function liveAgentClockLong(now: Date = new Date()): string {
  const day = teamDay(now);
  const spell = ([hour, period]: Clock) => `${hour}:00 ${period === 'am' ? 'a.m.' : 'p.m.'}`;
  return `${spell(central(day, TEAM_OPENS_HOUR))} to ${spell(central(day, TEAM_CLOSES_HOUR))}`;
}

/** "Mon to Fri, 11am to 7pm CST": the line most pages show. */
export function liveAgentHours(now: Date = new Date()): string {
  return `Mon to Fri, ${liveAgentClock(now)} CST`;
}

/**
 * A help article written in Kustomer says {team_hours} where the hours go, so the article
 * never needs editing when the clocks change.
 */
export const TEAM_HOURS_TOKEN = '{team_hours}';

export function withLiveAgentHours(html: string, now: Date = new Date()): string {
  return html.split(TEAM_HOURS_TOKEN).join(liveAgentHours(now));
}
